import React, { useState, useRef, useEffect } from 'react';
import CryptoJS from 'crypto-js';
import { api } from '../services/api';
import { parse } from 'best-effort-json-parser';
import { setGlobalWebSocket } from '../hooks/useMetrics';

// Timeout constants
const PING_INTERVAL_MS = 60000; // 60 seconds
const TYPING_TIMEOUT_MS = 2000; // 2 seconds

interface WebSocketCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  websocketUrl: string;
  region: string;
}

interface Recommendation {
  type: string;
  summary: string;
  recommendation: string[];
  followup_questions_to_ask_customer: string[];
  next_best_actions_by_agent: string[];
  tools_used: string[];
}

interface SummaryEvent {
  problemStatement?: string;
  problemStatementExt?: string;
  tools_used?: string[];
  relevantDetails?: string | string[];
  customer_facing_suggested_response?: string;
}

interface WebSocketManagerProps {
  contactId: string;
  onRecommendation: (recommendation: Recommendation) => void;
  onSummaryEvent: (summary: SummaryEvent) => void;
  onConnectionStatus: (status: 'disconnected' | 'connecting' | 'connected' | 'error') => void;
  onTypingStatus: (isTyping: boolean) => void;
  recommendationMode: 'live' | 'adhoc';
  onModeChangeComplete: () => void;
  onRequestAdhocRecommendationReady: (requestFn: () => void) => void;
}



export const WebSocketManager: React.FC<WebSocketManagerProps> = ({
  contactId,
  onRecommendation,
  onSummaryEvent,
  onConnectionStatus,
  onTypingStatus,
  recommendationMode,
  onModeChangeComplete,
  onRequestAdhocRecommendationReady,
}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const accumulatedDataRef = useRef<string>('');
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [currentMode, setCurrentMode] = useState<'live' | 'adhoc'>('adhoc');

  // Send recommendation mode change message
  const sendModeChange = (mode: 'live' | 'adhoc') => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: 'set_recommendation_mode',
        contact_id: contactId,
        mode: mode
      };
      wsRef.current.send(JSON.stringify(message));
      setCurrentMode(mode);
    }
  };

  // Send adhoc recommendation request
  const requestAdhocRecommendation = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: 'get_adhoc_recommendation',
        contact_id: contactId
      };
      wsRef.current.send(JSON.stringify(message));
    }
  };

  // Effect to handle mode changes from parent
  useEffect(() => {
    if (recommendationMode !== currentMode && wsRef.current?.readyState === WebSocket.OPEN) {
      sendModeChange(recommendationMode);
    }
  }, [recommendationMode, currentMode, contactId]);

  // Expose methods to parent component via callback
  useEffect(() => {
    onRequestAdhocRecommendationReady(requestAdhocRecommendation);
  }, [contactId, onRequestAdhocRecommendationReady]);

  const signWebSocketUrl = (credentials: WebSocketCredentials): string => {
    // Validate required credentials
    if (!credentials.accessKeyId || !credentials.secretAccessKey || !credentials.region || !credentials.websocketUrl) {
      throw new Error('Missing required WebSocket credentials');
    }

    // Convert HTTPS URL to WSS format
    const wsUrl = credentials.websocketUrl.replace('https://', 'wss://');
    const url = new URL(wsUrl);
    const host = url.host;
    const path = url.pathname;
    const region = credentials.region;
    
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:\-]|\.\d{3}/g, '');
    const date = timestamp.substr(0, 8);
    
    const params: Record<string, string> = {
      'X-Amz-Algorithm': 'AWS4-HMAC-SHA256',
      'X-Amz-Credential': `${credentials.accessKeyId}/${date}/${region}/execute-api/aws4_request`,
      'X-Amz-Date': timestamp,
      'X-Amz-SignedHeaders': 'host',
      'contact_id': contactId
    };
    
    if (credentials.sessionToken) {
      params['X-Amz-Security-Token'] = credentials.sessionToken;
    }
    
    const canonicalQuery = Object.keys(params)
      .sort()
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
    
    const canonicalRequest = `GET\n${path}\n${canonicalQuery}\nhost:${host}\n\nhost\n${CryptoJS.SHA256('').toString()}`;
    const stringToSign = `AWS4-HMAC-SHA256\n${timestamp}\n${date}/${region}/execute-api/aws4_request\n${CryptoJS.SHA256(canonicalRequest).toString()}`;
    
    const kDate = CryptoJS.HmacSHA256(date, 'AWS4' + credentials.secretAccessKey);
    const kRegion = CryptoJS.HmacSHA256(region, kDate);
    const kService = CryptoJS.HmacSHA256('execute-api', kRegion);
    const kSigning = CryptoJS.HmacSHA256('aws4_request', kService);
    const signature = CryptoJS.HmacSHA256(stringToSign, kSigning).toString();
    
    params['X-Amz-Signature'] = signature;
    
    const finalQuery = Object.keys(params)
      .sort()
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
    
    return `${wsUrl}?${finalQuery}`;
  };

  const connect = async () => {
    try {
      onConnectionStatus('connecting');
      
      const credentials = await api.getWebSocketCredentials();
      if (!credentials) {
        throw new Error('Failed to get WebSocket credentials');
      }

      const signedUrl = signWebSocketUrl(credentials);
      const ws = new WebSocket(signedUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        onConnectionStatus('connected');
        accumulatedDataRef.current = '';
        setGlobalWebSocket(ws);

        // Always request the previous-interaction summary on connect (including on
        // reload/refresh). The /initializeCustomerState `refresh` signal that used to
        // suppress this is gone, so there is no dedup here.
        console.log('Requesting previous-interaction summary for contact:', contactId);
        api.summarizeCustomerPreviousInteraction(contactId);

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket received message:', data);
          
          if (['transcript_chunk', 'adhoc_recommendation', 'summary_chunk'].includes(data.type)) {
            console.log('Processing message type:', data.type);
            // Reset typing timeout
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            
            // Accumulate streaming data - handle SSE format
            let content = data.data;
            
            // Parse SSE format: "data: {...}"
            if (content.startsWith('data: ')) {
              try {
                const sseData = JSON.parse(content.substring(6));
                if (sseData.type === 'streamed_response' && sseData.content) {
                  accumulatedDataRef.current += sseData.content;
                }
              } catch (e) {
                // If not valid SSE JSON, treat as raw content
                accumulatedDataRef.current += content;
              }
            } else {
              accumulatedDataRef.current += content;
            }
            if (data.type === 'summary_chunk') {
              const firstBraceIndex = accumulatedDataRef.current.indexOf('{');
              if (firstBraceIndex !== -1) {
                const jsonSubstring = accumulatedDataRef.current.substring(firstBraceIndex);
                const parsedSummaryChunk = parse(jsonSubstring) as Partial<SummaryEvent>;
                if(parsedSummaryChunk){
                  onTypingStatus(true);
                  onSummaryEvent(parsedSummaryChunk as SummaryEvent);
                  onTypingStatus(false);
                }
              }
            }

            
            // Try to extract complete JSON objects
            const jsonPattern = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
            const matches = accumulatedDataRef.current.match(jsonPattern);
            
            if (matches) {
              matches.forEach(match => {
                try {
                  const parsedData = JSON.parse(match);
                  
                  if (data.type === 'summary_chunk') {
                    console.log('Found summary_chunk:', parsedData);
                    // Handle summary type
                    const enrichedSummary = {
                      ...parsedData,
                      timestamp: new Date().toISOString()
                    };
                    onTypingStatus(true);
                    onSummaryEvent(enrichedSummary);
                    onTypingStatus(false);
                    accumulatedDataRef.current = accumulatedDataRef.current.replace(match, '');
                  } else if (parsedData.type && parsedData.summary) {
                    // Handle recommendation type
                    const enrichedRecommendation = {
                      ...parsedData,
                      timestamp: new Date().toISOString(),
                      isAdhoc: data.type === 'adhoc_recommendation'
                    };
                    
                    if (parsedData.type !== 'no_useful_info') {
                      onTypingStatus(true);
                      onRecommendation(enrichedRecommendation);
                      onTypingStatus(false);
                    }
                    accumulatedDataRef.current = accumulatedDataRef.current.replace(match, '');
                  }
                } catch (e) {
                  // Invalid JSON, continue accumulating
                }
              });
            }
            
            // Stop typing indicator after 2 seconds of no new chunks
            typingTimeoutRef.current = setTimeout(() => {
              onTypingStatus(false);
            }, TYPING_TIMEOUT_MS);
          } else if (data.statusCode) {
            // Handle mode change responses
            if (data.statusCode === 200) {
              onModeChangeComplete();
            }
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        onConnectionStatus('disconnected');
        accumulatedDataRef.current = '';
        setGlobalWebSocket(null);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      };

      ws.onerror = () => {
        onConnectionStatus('error');
        accumulatedDataRef.current = '';
      };

    } catch (error) {
      console.error('WebSocket connection error:', error);
      onConnectionStatus('error');
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    onTypingStatus(false);
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  useEffect(() => {
    if (contactId) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [contactId]);

  return null; // This is a headless component
};
